import { useMemo, useState } from "react";
import { isProfileSyncing } from "@/lib/profileSyncState";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, BedDouble, Dumbbell, ShieldCheck, CalendarDays,
  Brain, HeartPulse, Zap, Coffee, Play, Eye, Loader2, ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { PrioritizedAlert } from "@/lib/alertPriority";
import { openBrainChat } from "@/components/brain/ChatContainer";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SessionViewer } from "@/components/sessions/SessionViewer";
import { useActiveSession } from "@/hooks/useActiveSession";

function askBrain(prompt: string) {
  openBrainChat();
  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent("brain-inject-prompt", { detail: { prompt } })
    );
  }, 150);
}

function openPlanning() {
  window.location.href = "/planning";
}

function getRecoveryOneLiner(fatigueScore: number | null): string {
  const score = fatigueScore ?? 5;
  if (score >= 7) return "Repos actif : mobilité douce et étirements légers, pas d'intensité.";
  if (score >= 5) return "Séance allégée : réduis le volume de 30-40%, privilégie la technique.";
  return "Tu peux t'entraîner normalement — pense à bien t'échauffer.";
}

interface ActionItem {
  icon: typeof ArrowRight;
  label: string;
  description: string;
  action: () => void;
  variant: "primary" | "secondary" | "muted";
  isRecovery?: boolean;
}

interface Props {
  alert: PrioritizedAlert;
  fatigueScore: number | null;
  hasProgram: boolean;
  hasPlanning: boolean;
  onClose: () => void;
}

export default function StatusActionPanel({
  alert, fatigueScore, hasProgram, hasPlanning, onClose,
}: Props) {
  const [generatingSession, setGeneratingSession] = useState(false);
  const [generatedSession, setGeneratedSession] = useState<any>(null);
  const [showViewer, setShowViewer] = useState(false);
  const { startSession } = useActiveSession();
  const recoveryOneLiner = useMemo(() => getRecoveryOneLiner(fatigueScore), [fatigueScore]);

  const actions = useMemo((): ActionItem[] => {
    const items: ActionItem[] = [];

    switch (alert.category) {
      case "health":
        items.push({
          icon: HeartPulse,
          label: "Adapter ma séance",
          description: "Modifier la séance du jour selon la douleur signalée",
          action: () => {
            onClose();
            askBrain("J'ai une douleur signalée récemment. Que dois-je adapter dans ma séance du jour ? Explique pourquoi.");
          },
          variant: "primary",
        });
        items.push({
          icon: BedDouble,
          label: "Jour de repos",
          description: recoveryOneLiner,
          action: () => {},
          variant: "secondary",
          isRecovery: true,
        });
        break;

      case "overload":
        items.push({
          icon: Coffee,
          label: "Récupération active",
          description: recoveryOneLiner,
          action: () => {},
          variant: "primary",
          isRecovery: true,
        });
        items.push({
          icon: CalendarDays,
          label: "Voir mon planning",
          description: "Réorganiser les créneaux de la semaine",
          action: () => {
            onClose();
            openPlanning();
          },
          variant: "secondary",
        });
        break;

      case "incoherence":
        if (!hasProgram) {
          items.push({
            icon: Dumbbell,
            label: "Trouver un programme",
            description: "Le Cerveau peut t'aider à choisir",
            action: () => {
              onClose();
              askBrain("Je n'ai pas de programme actif. Aide-moi à comprendre quel type de programme me conviendrait et pourquoi.");
            },
            variant: "primary",
          });
        } else {
          items.push({
            icon: CalendarDays,
            label: "Ajuster le planning",
            description: "Adapter les créneaux à la semaine en cours",
            action: () => {
              onClose();
              openPlanning();
            },
            variant: "primary",
          });
        }
        break;

      case "missing_data":
        if (!hasPlanning) {
          items.push({
            icon: CalendarDays,
            label: "Configurer mon planning",
            description: "Définir tes créneaux de la semaine",
            action: () => {
              onClose();
              openPlanning();
            },
            variant: "primary",
          });
        }
        items.push({
          icon: ShieldCheck,
          label: "Compléter mon check-in",
          description: "Renseigner entraînement, nutrition, sommeil",
          action: () => {
            onClose();
          },
          variant: items.length === 0 ? "primary" : "secondary",
        });
        break;

      default:
        items.push({
          icon: Brain,
          label: "Demander au Cerveau",
          description: "Obtenir une recommandation personnalisée",
          action: () => {
            onClose();
            askBrain(`Mon statut actuel : "${alert.label}" — ${alert.message}. Que dois-je faire maintenant et pourquoi ?`);
          },
          variant: "primary",
        });
    }

    if (!items.some((i) => i.icon === Brain)) {
      items.push({
        icon: Brain,
        label: "Demander au Cerveau",
        description: "Poser une question contextuelle",
        action: () => {
          onClose();
          askBrain(`Mon statut : "${alert.label}" — ${alert.message}. Explique-moi d'où ça vient et que faire.`);
        },
        variant: "muted",
      });
    }

    return items;
  }, [alert, fatigueScore, hasProgram, hasPlanning, onClose, recoveryOneLiner]);

  const urgencyColor = alert.priority <= 1
    ? "border-destructive/40"
    : alert.priority <= 2
      ? "border-amber-500/40"
      : "border-border";

  const handleCreateSession = async () => {
    if (isProfileSyncing()) {
      toast.info("Synchronisation du profil en cours, patiente…");
      return;
    }
    setGeneratingSession(true);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession?.access_token;
      if (!token) { toast.error("Connexion requise"); setGeneratingSession(false); return; }
      const score = fatigueScore ?? 5;
      const constraint = score >= 7
        ? "Séance de récupération active : mobilité douce, étirements, pas d'intensité. 15-20 min max."
        : "Séance allégée : réduire le volume de 30-40%, privilégier la technique et la mobilité. 25-30 min.";
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ prompt: `Récupération active. ${constraint}`, duration_minutes: score >= 7 ? 20 : 30, user_level: "Débutant" }),
        }
      );
      if (!resp.ok) { toast.error("Erreur génération"); setGeneratingSession(false); return; }
      const data = await resp.json();
      // Normalize to match SessionRecord shape
      const session = {
        id: crypto.randomUUID(),
        user_id: "",
        title: data.title || "Séance de récupération",
        objective: data.objective || "",
        exercises: data.exercises || [],
        duration_minutes: data.duration_minutes || 30,
        level_required: data.level_required || "Débutant",
        contraindications: data.contraindications || [],
        warnings: data.warnings || [],
        fact_ids: data.fact_ids || [],
        tags: data.tags || [],
        mode: "ai",
        status: "draft",
        is_favorite: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setGeneratedSession(session);
      setShowViewer(true);
      toast.success("Séance créée !");
    } catch {
      toast.error("Erreur de connexion");
    }
    setGeneratingSession(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scaleY: 0.95 }}
      animate={{ opacity: 1, y: 0, scaleY: 1 }}
      exit={{ opacity: 0, y: -8, scaleY: 0.95 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        "rounded-xl border bg-card shadow-xl p-3 space-y-2",
        urgencyColor,
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <Zap className={cn(
          "h-3.5 w-3.5",
          alert.priority <= 1 ? "text-destructive" : alert.priority <= 2 ? "text-amber-500" : "text-primary",
        )} />
        <p className="text-xs font-bold text-foreground flex-1">
          Que faire maintenant ?
        </p>
        <span className={cn(
          "text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded",
          alert.priority <= 1 ? "bg-destructive/10 text-destructive"
            : alert.priority <= 2 ? "bg-amber-500/10 text-amber-500"
            : "bg-muted text-muted-foreground",
        )}>
          {alert.priority <= 1 ? "Urgent" : alert.priority <= 2 ? "Important" : "Info"}
        </span>
      </div>

      {/* Actions */}
      <div className="space-y-1.5">
        {actions.map((item, i) => {
          const Icon = item.icon;

          if (item.isRecovery) {
            return (
              <div
                key={i}
                className={cn(
                  "w-full rounded-lg px-3 py-2.5",
                  item.variant === "primary"
                    ? "bg-primary/10 border border-primary/20"
                    : "bg-secondary/50 border border-border",
                )}
              >
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "rounded-lg p-1.5 shrink-0",
                    item.variant === "primary" ? "bg-primary/15" : "bg-muted",
                  )}>
                    <Icon className={cn(
                      "h-4 w-4",
                      item.variant === "primary" ? "text-primary" : "text-muted-foreground",
                    )} />
                  </div>
                  <p className="text-[11px] text-foreground leading-snug flex-1 min-w-0">{item.description}</p>
                  <div className="flex gap-1.5 shrink-0">
                    {!generatedSession && (
                      <button
                        disabled={generatingSession}
                        onClick={handleCreateSession}
                        className="flex items-center gap-1 rounded-md bg-primary/15 hover:bg-primary/20 border border-primary/20 px-2 py-1 text-[10px] font-semibold text-primary transition-colors disabled:opacity-50"
                      >
                        {generatingSession
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <><Dumbbell className="h-3 w-3" /> Créer</>
                        }
                      </button>
                    )}
                    {generatedSession && (
                      <>
                        <button
                          onClick={() => setShowViewer(true)}
                          className="flex items-center gap-1 rounded-md bg-secondary hover:bg-secondary/80 border border-border px-2 py-1 text-[10px] font-semibold text-foreground transition-colors"
                        >
                          <Eye className="h-3 w-3" /> Voir
                        </button>
                        <button
                          onClick={() => {
                            onClose();
                            startSession(generatedSession);
                          }}
                          className="flex items-center gap-1 rounded-md bg-primary/15 hover:bg-primary/20 border border-primary/20 px-2 py-1 text-[10px] font-semibold text-primary transition-colors"
                        >
                          <Play className="h-3 w-3" /> Go
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <button
              key={i}
              onClick={item.action}
              className={cn(
                "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all group",
                item.variant === "primary"
                  ? "bg-primary/10 hover:bg-primary/15 border border-primary/20"
                  : item.variant === "secondary"
                    ? "bg-secondary/50 hover:bg-secondary border border-border"
                    : "hover:bg-muted/50",
              )}
            >
              <div className={cn(
                "rounded-lg p-1.5 shrink-0",
                item.variant === "primary" ? "bg-primary/15" : "bg-muted",
              )}>
                <Icon className={cn(
                  "h-4 w-4",
                  item.variant === "primary" ? "text-primary" : "text-muted-foreground",
                )} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "text-xs font-semibold",
                  item.variant === "primary" ? "text-primary" : "text-foreground",
                )}>
                  {item.label}
                </p>
                <p className="text-[10px] text-muted-foreground leading-tight truncate">
                  {item.description}
                </p>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0" />
            </button>
          );
        })}
      </div>

      {/* Inline SessionViewer after creation */}
      <AnimatePresence>
      {showViewer && generatedSession && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowViewer(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="w-[95vw] max-w-lg max-h-[80dvh] overflow-y-auto rounded-xl border border-border bg-card shadow-2xl p-3"
              onClick={(e) => e.stopPropagation()}
            >
              <SessionViewer
                session={generatedSession}
                onBack={() => setShowViewer(false)}
              />
            </motion.div>
          </div>
      )}
      </AnimatePresence>

      {/* Justification */}
      <p className="text-[9px] text-muted-foreground/70 pt-1 border-t border-border leading-tight">
        <span className="font-semibold">Pourquoi :</span> {alert.message}
        {alert.silenced.length > 0 && ` (+${alert.silenced.length} autre${alert.silenced.length > 1 ? "s" : ""} signal${alert.silenced.length > 1 ? "aux" : ""})`}
      </p>
    </motion.div>
  );
}
